import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { useAuthStore, UserRole } from '@/store/authStore';
import { isSessionExpired } from '@timetable/api';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: UserRole[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, role, user } = useAuthStore();

  // Not authenticated, or token is expired with no valid refresh possible
  if (!isAuthenticated || isSessionExpired()) {
    return <Navigate to="/login" replace />;
  }

  // Block pending/rejected users from accessing the app
  const status = (user as any)?.status;
  if (status === 'pending' || status === 'rejected') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          p: 3,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {status === 'pending' ? 'Approval Pending' : 'Account Rejected'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {status === 'pending'
            ? 'Your account is awaiting admin approval. Please check back later.'
            : 'Your account request was not approved. Please contact your school admin.'}
        </Typography>
        <Button variant="outlined" href="/login">
          Back to Login
        </Button>
      </Box>
    );
  }

  // Check role-based access
  if (requiredRoles && requiredRoles.length > 0) {
    if (!role || !requiredRoles.includes(role)) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You do not have permission to access this page.
          </Typography>
          <Button variant="contained" href="/app/dashboard">
            Go to Dashboard
          </Button>
        </Box>
      );
    }
  }

  return <>{children}</>;
}
